import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Target, Plus, Trash2, Trophy, TrendingUp, Save, X } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import api from '../lib/api';

const fmtCurrency = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n || 0);

const TIER_BADGE = {
  stretch: { color: 'bg-purple-100 text-purple-700', icon: '🚀', label: 'Stretch' },
  achieved: { color: 'bg-amber-100 text-amber-700', icon: '🏆', label: 'Achieved' },
  on_track: { color: 'bg-emerald-100 text-emerald-700', icon: '✓', label: 'On Track' },
  below_par: { color: 'bg-red-100 text-red-700', icon: '⚠', label: 'Below Par' },
};

const getMonthRange = (year, month) => ({
  start: `${year}-${String(month).padStart(2, '0')}-01`,
  end: new Date(year, month, 0).toISOString().slice(0, 10), // last day of month
});
const getQuarterRange = (year, q) => {
  const startMonth = (q - 1) * 3 + 1;
  return {
    start: `${year}-${String(startMonth).padStart(2, '0')}-01`,
    end: new Date(year, startMonth + 2, 0).toISOString().slice(0, 10),
  };
};

const SalesTargets = ({ user }) => {
  const [tab, setTab] = useState('targets');
  const [salesmen, setSalesmen] = useState([]);
  const [targets, setTargets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [form, setForm] = useState(null);

  // Period selector
  const now = new Date();
  const [periodType, setPeriodType] = useState('monthly');
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [quarter, setQuarter] = useState(Math.ceil((now.getMonth() + 1) / 3));

  const [leaderboard, setLeaderboard] = useState(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [s, t] = await Promise.all([
        api.get('/targets/salesmen'),
        api.get(`/targets?period_type=${periodType}`),
      ]);
      setSalesmen(s.data || []);
      setTargets(t.data || []);
    } catch (err) { toast.error('Failed to load targets'); }
    finally { setLoading(false); }
  }, [periodType]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const loadLeaderboard = useCallback(async () => {
    try {
      const range = periodType === 'monthly' ? getMonthRange(year, month) : getQuarterRange(year, quarter);
      const res = await api.get(`/targets/leaderboard?period_type=${periodType}&period_start=${range.start}&period_end=${range.end}`);
      setLeaderboard(res.data);
    } catch { toast.error('Failed to load leaderboard'); }
  }, [periodType, year, month, quarter]);

  useEffect(() => { if (tab === 'leaderboard') loadLeaderboard(); }, [tab, loadLeaderboard]);

  const openNewTarget = () => {
    const range = periodType === 'monthly' ? getMonthRange(year, month) : getQuarterRange(year, quarter);
    setForm({
      salesman_id: '',
      period_type: periodType,
      period_start: range.start,
      period_end: range.end,
      target_sales: '',
      commission_slab_type: 'flat',
      commission_slabs: [{ from_amount: 0, to_amount: '', percentage: 0 }],
      notes: '',
    });
    setShowDialog(true);
  };

  const editTarget = (t) => {
    setForm({
      ...t,
      target_sales: t.target_sales,
      commission_slabs: (t.commission_slabs || []).length ? t.commission_slabs : [{ from_amount: 0, to_amount: '', percentage: 0 }],
    });
    setShowDialog(true);
  };

  const updateSlab = (idx, key, val) => {
    setForm(f => ({
      ...f,
      commission_slabs: f.commission_slabs.map((s, i) => i === idx ? { ...s, [key]: val } : s),
    }));
  };
  const addSlab = () => setForm(f => ({ ...f, commission_slabs: [...f.commission_slabs, { from_amount: 0, to_amount: '', percentage: 0 }] }));
  const removeSlab = (idx) => setForm(f => ({ ...f, commission_slabs: f.commission_slabs.filter((_, i) => i !== idx) }));

  const saveTarget = async () => {
    if (!form.salesman_id) { toast.error('Select a salesman'); return; }
    if (!form.target_sales || form.target_sales <= 0) { toast.error('Enter a target amount'); return; }
    const slabs = form.commission_slabs.map(s => ({
      from_amount: parseFloat(s.from_amount) || 0,
      to_amount: s.to_amount === '' || s.to_amount === null ? null : parseFloat(s.to_amount),
      percentage: parseFloat(s.percentage) || 0,
    }));
    const payload = {
      salesman_id: form.salesman_id,
      period_type: form.period_type,
      period_start: form.period_start,
      period_end: form.period_end,
      target_sales: parseFloat(form.target_sales),
      commission_slab_type: form.commission_slab_type,
      commission_slabs: slabs,
      notes: form.notes || null,
    };
    try {
      if (form.id) {
        await api.put(`/targets/${form.id}`, payload);
        toast.success('Target updated');
      } else {
        await api.post('/targets', payload);
        toast.success('Target created');
      }
      setShowDialog(false);
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    }
  };

  const deleteTarget = async (t) => {
    if (!window.confirm(`Delete target for ${t.salesman_name}?`)) return;
    try {
      await api.delete(`/targets/${t.id}`);
      toast.success('Target deleted');
      loadAll();
    } catch { toast.error('Delete failed'); }
  };

  if (user?.role !== 'admin') return <div className="p-8 text-center text-gray-500">Admin only</div>;

  return (
    <div className="p-6 space-y-6 animate-fadeIn" data-testid="sales-targets-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Target className="w-7 h-7 text-indigo-600" />
          <div>
            <h1 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Nunito' }}>Sales Targets</h1>
            <p className="text-sm text-gray-500 mt-0.5">Per-salesman monthly/quarterly targets with commission slabs</p>
          </div>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="targets" data-testid="tab-targets">Set Targets</TabsTrigger>
          <TabsTrigger value="leaderboard" data-testid="tab-leaderboard"><Trophy className="w-4 h-4 mr-1" /> Leaderboard</TabsTrigger>
        </TabsList>

        <TabsContent value="targets" className="space-y-4 mt-4">
          <Card>
            <CardContent className="p-3 flex flex-wrap items-center gap-3">
              <Label className="text-xs">Period:</Label>
              <Select value={periodType} onValueChange={setPeriodType}>
                <SelectTrigger className="w-32 h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="quarterly">Quarterly</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={openNewTarget} className="ml-auto bg-indigo-600 hover:bg-indigo-700" size="sm" data-testid="add-target-btn">
                <Plus className="w-4 h-4 mr-1" /> Add Target
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="targets-table">
                  <thead><tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-3 py-2 text-left text-xs font-medium">Salesman</th>
                    <th className="px-3 py-2 text-center text-xs font-medium">Period</th>
                    <th className="px-3 py-2 text-right text-xs font-medium">Target</th>
                    <th className="px-3 py-2 text-right text-xs font-medium">Achieved</th>
                    <th className="px-3 py-2 text-center text-xs font-medium">%</th>
                    <th className="px-3 py-2 text-right text-xs font-medium">Commission</th>
                    <th className="px-3 py-2 text-center text-xs font-medium">Slab</th>
                    <th className="px-3 py-2 text-center text-xs font-medium">Action</th>
                  </tr></thead>
                  <tbody>
                    {targets.length === 0 ? (
                      <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-400">No targets set yet. Click "Add Target" to start.</td></tr>
                    ) : targets.map(t => (
                      <tr key={t.id} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`target-row-${t.id}`}>
                        <td className="px-3 py-2 text-xs font-medium">{t.salesman_name}</td>
                        <td className="px-3 py-2 text-center text-xs text-gray-500">{t.period_start.slice(5)} → {t.period_end.slice(5)}</td>
                        <td className="px-3 py-2 text-right text-xs font-medium">{fmtCurrency(t.target_sales)}</td>
                        <td className="px-3 py-2 text-right text-xs font-medium text-emerald-700">{fmtCurrency(t.achievement?.net_sales || 0)}</td>
                        <td className="px-3 py-2 text-center text-xs">
                          <span className={`px-2 py-0.5 rounded-md font-bold ${
                            t.achievement_pct >= 100 ? 'bg-emerald-100 text-emerald-700' :
                            t.achievement_pct >= 70 ? 'bg-amber-100 text-amber-700' :
                            'bg-red-100 text-red-700'
                          }`}>{t.achievement_pct}%</span>
                        </td>
                        <td className="px-3 py-2 text-right text-xs font-bold text-indigo-700">{fmtCurrency(t.commission?.total || 0)}</td>
                        <td className="px-3 py-2 text-center"><Badge className="text-[9px] bg-gray-200 text-gray-600">{t.commission_slab_type}</Badge></td>
                        <td className="px-3 py-2 text-center flex gap-1 justify-center">
                          <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={() => editTarget(t)} data-testid={`edit-target-${t.id}`}>Edit</Button>
                          <Button size="sm" variant="ghost" className="h-6 px-2 text-xs text-red-600" onClick={() => deleteTarget(t)} data-testid={`del-target-${t.id}`}><Trash2 className="w-3 h-3" /></Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="leaderboard" className="space-y-4 mt-4">
          <Card>
            <CardContent className="p-3 flex flex-wrap items-center gap-2">
              <Label className="text-xs">Period:</Label>
              <Select value={periodType} onValueChange={setPeriodType}>
                <SelectTrigger className="w-32 h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="quarterly">Quarterly</SelectItem>
                </SelectContent>
              </Select>
              {periodType === 'monthly' ? (
                <>
                  <Input type="number" value={year} onChange={e => setYear(parseInt(e.target.value))} className="h-8 w-24 text-xs" data-testid="lb-year" />
                  <Select value={String(month)} onValueChange={v => setMonth(parseInt(v))}>
                    <SelectTrigger className="w-32 h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'].map((m, i) =>
                        <SelectItem key={i} value={String(i+1)}>{m}</SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </>
              ) : (
                <>
                  <Input type="number" value={year} onChange={e => setYear(parseInt(e.target.value))} className="h-8 w-24 text-xs" data-testid="lb-year-q" />
                  <Select value={String(quarter)} onValueChange={v => setQuarter(parseInt(v))}>
                    <SelectTrigger className="w-32 h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">Q1 (Apr-Jun)</SelectItem>
                      <SelectItem value="2">Q2 (Jul-Sep)</SelectItem>
                      <SelectItem value="3">Q3 (Oct-Dec)</SelectItem>
                      <SelectItem value="4">Q4 (Jan-Mar)</SelectItem>
                    </SelectContent>
                  </Select>
                </>
              )}
              <Button size="sm" onClick={loadLeaderboard} className="ml-auto bg-indigo-600 hover:bg-indigo-700" data-testid="reload-lb">Load</Button>
            </CardContent>
          </Card>

          {leaderboard && (
            <>
              <div className="grid grid-cols-3 gap-3">
                <Card><CardContent className="p-4 text-center"><p className="text-xs text-gray-500">Total Target</p><p className="text-lg font-bold">{fmtCurrency(leaderboard.totals.target_total)}</p></CardContent></Card>
                <Card><CardContent className="p-4 text-center"><p className="text-xs text-gray-500">Total Achievement</p><p className="text-lg font-bold text-emerald-700">{fmtCurrency(leaderboard.totals.achievement_total)}</p></CardContent></Card>
                <Card><CardContent className="p-4 text-center"><p className="text-xs text-gray-500">Total Commission</p><p className="text-lg font-bold text-indigo-700">{fmtCurrency(leaderboard.totals.commission_total)}</p></CardContent></Card>
              </div>
              <Card>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="leaderboard-table">
                      <thead><tr className="bg-gray-50 border-b">
                        <th className="px-3 py-2 text-center text-xs font-medium w-[50px]">Rank</th>
                        <th className="px-3 py-2 text-left text-xs font-medium">Salesman</th>
                        <th className="px-3 py-2 text-right text-xs font-medium">Target</th>
                        <th className="px-3 py-2 text-right text-xs font-medium">Achieved</th>
                        <th className="px-3 py-2 text-right text-xs font-medium">Gap</th>
                        <th className="px-3 py-2 text-center text-xs font-medium">Bills</th>
                        <th className="px-3 py-2 text-center text-xs font-medium">%</th>
                        <th className="px-3 py-2 text-center text-xs font-medium">Tier</th>
                        <th className="px-3 py-2 text-right text-xs font-medium">Commission</th>
                      </tr></thead>
                      <tbody>
                        {leaderboard.leaderboard.map((r, i) => {
                          const tier = TIER_BADGE[r.tier] || TIER_BADGE.below_par;
                          return (
                            <tr key={r.target_id} className={`border-b ${i === 0 ? 'bg-amber-50' : ''}`}>
                              <td className="px-3 py-2 text-center text-xs font-bold">{i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : i + 1}</td>
                              <td className="px-3 py-2 text-xs font-medium">{r.salesman_name}</td>
                              <td className="px-3 py-2 text-right text-xs">{fmtCurrency(r.target_sales)}</td>
                              <td className="px-3 py-2 text-right text-xs font-medium text-emerald-700">{fmtCurrency(r.net_sales)}</td>
                              <td className={`px-3 py-2 text-right text-xs ${r.gap > 0 ? 'text-red-600' : 'text-emerald-700'}`}>{r.gap > 0 ? `-${fmtCurrency(r.gap)}` : `+${fmtCurrency(Math.abs(r.gap))}`}</td>
                              <td className="px-3 py-2 text-center text-xs">{r.bills}</td>
                              <td className="px-3 py-2 text-center text-xs font-bold">{r.achievement_pct}%</td>
                              <td className="px-3 py-2 text-center"><Badge className={`text-[10px] ${tier.color}`}>{tier.icon} {tier.label}</Badge></td>
                              <td className="px-3 py-2 text-right text-xs font-bold text-indigo-700">{fmtCurrency(r.commission_total)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>
      </Tabs>

      {/* Add/Edit Target dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="target-dialog">
          <DialogHeader><DialogTitle>{form?.id ? 'Edit' : 'Add'} Sales Target</DialogTitle></DialogHeader>
          {form && (
            <div className="space-y-4 mt-2">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Salesman</Label>
                  <Select value={form.salesman_id} onValueChange={v => setForm({ ...form, salesman_id: v })} disabled={!!form.id}>
                    <SelectTrigger data-testid="form-salesman"><SelectValue placeholder="Select salesman" /></SelectTrigger>
                    <SelectContent>{salesmen.map(s => <SelectItem key={s.id} value={s.id}>{s.full_name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Period Type</Label>
                  <Select value={form.period_type} onValueChange={v => setForm({ ...form, period_type: v })} disabled={!!form.id}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="monthly">Monthly</SelectItem>
                      <SelectItem value="quarterly">Quarterly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Start Date</Label>
                  <Input type="date" value={form.period_start} onChange={e => setForm({ ...form, period_start: e.target.value })} data-testid="form-start" />
                </div>
                <div>
                  <Label className="text-xs">End Date</Label>
                  <Input type="date" value={form.period_end} onChange={e => setForm({ ...form, period_end: e.target.value })} data-testid="form-end" />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs">Target Sales (₹)</Label>
                  <Input type="number" value={form.target_sales} onChange={e => setForm({ ...form, target_sales: e.target.value })} placeholder="500000" data-testid="form-target-amt" />
                </div>
              </div>

              <div>
                <Label className="text-xs">Commission Slab Type</Label>
                <Select value={form.commission_slab_type} onValueChange={v => setForm({ ...form, commission_slab_type: v })}>
                  <SelectTrigger data-testid="form-slab-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="flat">Flat (one % above threshold)</SelectItem>
                    <SelectItem value="cumulative">Cumulative (% per band)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-[10px] text-gray-500 mt-1">
                  {form.commission_slab_type === 'flat'
                    ? 'Pick the highest slab whose threshold is reached → applies its % to the entire achievement.'
                    : 'Each slab\'s % applies only to the portion of achievement falling inside that band.'}
                </p>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-xs font-semibold">Commission Slabs</Label>
                  <Button size="sm" variant="outline" onClick={addSlab} className="h-7 text-xs" data-testid="add-slab"><Plus className="w-3 h-3 mr-1" /> Slab</Button>
                </div>
                <div className="space-y-2">
                  {form.commission_slabs.map((s, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`slab-row-${i}`}>
                      <div className="col-span-4">
                        <Input type="number" placeholder="From ₹" value={s.from_amount} onChange={e => updateSlab(i, 'from_amount', e.target.value)} className="h-8 text-xs" />
                      </div>
                      <div className="col-span-4">
                        <Input type="number" placeholder="To ₹ (blank = ∞)" value={s.to_amount === null ? '' : s.to_amount} onChange={e => updateSlab(i, 'to_amount', e.target.value)} className="h-8 text-xs" />
                      </div>
                      <div className="col-span-3">
                        <Input type="number" step="0.5" placeholder="%" value={s.percentage} onChange={e => updateSlab(i, 'percentage', e.target.value)} className="h-8 text-xs" />
                      </div>
                      <Button size="sm" variant="ghost" onClick={() => removeSlab(i)} className="col-span-1 h-8 text-red-500 px-1"><X className="w-3 h-3" /></Button>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <Label className="text-xs">Notes</Label>
                <Input value={form.notes || ''} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Optional notes" />
              </div>

              <div className="flex gap-2 justify-end pt-2 border-t">
                <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
                <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={saveTarget} data-testid="save-target-btn"><Save className="w-4 h-4 mr-1" /> Save</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SalesTargets;
